import * as THREE from 'three'
import { viewerApi } from '@viewer/lib/viewerApi'
import { useAppStore } from '@core/store'

export interface VisibilityBlocker {
  modelId: string
  localId: number
  distance: number
  point: [number, number, number]
}

export interface VisibilityCheckResult {
  clear: boolean
  distance: number
  blockers: VisibilityBlocker[]
}

interface RayHitLike {
  modelId: string
  localId: number
  distance: number
  point: [number, number, number]
}

export function filterVisibilityBlockers(
  hits: RayHitLike[],
  excluded: Set<string>,
  targetDistance: number,
  tolerance = 0.02,
): VisibilityBlocker[] {
  return hits
    .filter(
      (hit) =>
        !excluded.has(`${hit.modelId}:${hit.localId}`) &&
        hit.distance > tolerance &&
        hit.distance < targetDistance - tolerance,
    )
    .sort((a, b) => a.distance - b.distance)
}

/** Metros com duas casas e vírgula decimal, para o texto do painel. */
export function formatMetres(m: number): string {
  return `${m.toFixed(2).replace('.', ',')} m`
}

interface CanvasLike {
  getBoundingClientRect: () => { left: number; top: number; width: number; height: number }
}

/** Camera and "mouse" for the line-of-sight raycast.
 *
 *  The Fragments raycaster reads `mouse` in CLIENT PIXELS (it subtracts
 *  the canvas rect and normalises by its size), not in normalised device
 *  coordinates. Passing (0, 0) aimed the ray at the top-left corner of
 *  the window, some 40 degrees off the line between the two centres. The
 *  camera looks from `source` to `target`, so the ray that matters is the
 *  one through the CENTRE of the canvas. */
export function lineOfSightRay(
  source: THREE.Vector3,
  target: THREE.Vector3,
  dom: CanvasLike,
): { camera: THREE.PerspectiveCamera; mouse: THREE.Vector2 } {
  const distance = source.distanceTo(target)
  const camera = new THREE.PerspectiveCamera(50, 1, 0.001, distance + 10)
  camera.position.copy(source)
  camera.up.set(0, 1, 0)
  // Looking straight up or down makes `up` parallel to the view.
  const dir = target.clone().sub(source).normalize()
  if (Math.abs(dir.y) > 0.999) camera.up.set(0, 0, 1)
  camera.lookAt(target)
  camera.updateProjectionMatrix()
  camera.updateMatrixWorld(true)
  const rect = dom.getBoundingClientRect()
  const mouse = new THREE.Vector2(
    rect.left + rect.width / 2,
    rect.top + rect.height / 2,
  )
  return { camera, mouse }
}

/** Check the straight line between two selected element centres. */
export async function checkSelectedLineOfSight(): Promise<VisibilityCheckResult> {
  const selected = selectedPair()
  if (!selected) throw new Error('Seleciona exatamente dois elementos.')
  const [a, b] = selected
  const [centerA] = await viewerApi.getWorldBoxCenters(a.modelId, [a.localId])
  const [centerB] = await viewerApi.getWorldBoxCenters(b.modelId, [b.localId])
  if (!centerA || !centerB) {
    throw new Error('Um dos elementos selecionados não tem geometria.')
  }
  const source = new THREE.Vector3(...centerA)
  const target = new THREE.Vector3(...centerB)
  const distance = source.distanceTo(target)
  if (distance < 0.001) throw new Error('Os centros dos elementos coincidem.')

  const world = viewerApi.getWorld()
  const dom = world?.renderer?.three.domElement
  if (!dom) throw new Error('O viewer ainda não está pronto.')
  const { camera, mouse } = lineOfSightRay(source, target, dom)

  const hits: RayHitLike[] = []
  for (const [modelId, model] of viewerApi.iterModels()) {
    const modelHits = await model
      .raycastAll({ camera, mouse, dom })
      .catch(() => null)
    for (const hit of modelHits ?? []) {
      hits.push({
        modelId,
        localId: hit.localId,
        distance: hit.rayDistance ?? hit.distance,
        point: [hit.point.x, hit.point.y, hit.point.z],
      })
    }
  }
  const excluded = new Set(selected.map((item) => `${item.modelId}:${item.localId}`))
  const blockers = filterVisibilityBlockers(hits, excluded, distance)
  return { clear: blockers.length === 0, distance, blockers }
}

function selectedPair():
  | [{ modelId: string; localId: number }, { modelId: string; localId: number }]
  | null {
  const targets = useAppStore.getState().selectedTargets
  return targets.length === 2 ? [targets[0], targets[1]] : null
}
